import QRCodeGenerator from 'qrcode';

function generateQrCode(canvas, text, width = 160) {
  return new Promise((res, rej) => { 
    QRCodeGenerator.toCanvas(canvas, text, { width }, (error, c) => {
      if (error) {
        return rej(error);
      }

      return res(c);
    });
  });
}

// New function to generate SecurePaper code with text below
function generateQrCodeWithText(canvas, text, bottomText = 'https://securepaper.app', width = 160) {
  return new Promise((resolve, reject) => {
    // First generate the SecurePaper code
    QRCodeGenerator.toCanvas(canvas, text, { width }, (error, qrCanvas) => {
      if (error) {
        return reject(error);
      }

      // Now add text below the SecurePaper code
      const ctx = qrCanvas.getContext('2d');
      
      // Calculate text dimensions
      const fontSize = Math.max(12, width * 0.08); // Increased from 0.06 to 0.08 and minimum from 10 to 12
      ctx.font = `bold ${fontSize}px Arial`; // Made font bold for better visibility
      ctx.fillStyle = '#333333'; // Darker color for better contrast
      ctx.textAlign = 'center';
      
      const textMetrics = ctx.measureText(bottomText);
      const textHeight = fontSize;
      const textMargin = 8;
      
      // Resize canvas to accommodate text
      const originalImageData = ctx.getImageData(0, 0, width, width);
      qrCanvas.height = width + textHeight + textMargin * 2;
      
      // Fill background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, qrCanvas.height);
      
      // Redraw the SecurePaper code
      ctx.putImageData(originalImageData, 0, 0);
      
      // Draw the text
      ctx.fillStyle = '#333333'; // Darker color for better contrast
      ctx.font = `bold ${fontSize}px Arial`; // Made font bold for better visibility
      ctx.textAlign = 'center';
      ctx.fillText(bottomText, width / 2, width + textMargin + textHeight);
      
      resolve(qrCanvas);
    });
  });
}

// New function to generate SecurePaper code with logo
function generateQrCodeWithLogo(canvas, text, logoSrc, width = 160, logoOptions = {}) {
  return new Promise((resolve, reject) => {
    // First generate the SecurePaper code with high error correction level
    QRCodeGenerator.toCanvas(canvas, text, { 
      width,
      errorCorrectionLevel: 'H' // High error correction to allow for logo space
    }, (error, canvas) => {
      if (error) {
        return reject(error);
      }

      // Add the logo
      const logo = new Image();
      logo.src = logoSrc;
      
      logo.onload = () => {
        const ctx = canvas.getContext('2d');
        
        // Default options with smaller logo
        const {
          logoSizeRatio = 0.15,    // Size as a ratio of SecurePaper code width (smaller than original 0.2)
          // Source image cropping (to just get the icon part)
          srcX = 0,                // Source X position to start cropping
          srcY = 0,                // Source Y position to start cropping
          srcWidth = logo.width,   // Width of source crop (default: full logo width)
          srcHeight = logo.height  // Height of source crop (default: full logo height)
        } = logoOptions;
        
        const logoSize = width * logoSizeRatio;
        const logoX = (width - logoSize) / 2;
        const logoY = (width - logoSize) / 2;
        
        // Draw white background for logo
        ctx.fillStyle = 'white';
        ctx.fillRect(logoX, logoY, logoSize, logoSize);
        
        // Draw logo - using the cropped portion if specified
        ctx.drawImage(
          logo, 
          srcX, srcY, srcWidth, srcHeight, // Source rectangle (cropping)
          logoX, logoY, logoSize, logoSize  // Destination rectangle (positioning)
        );
        
        resolve(canvas);
      };
      
      logo.onerror = () => {
        // If logo fails to load, just return the SecurePaper code without logo
        resolve(canvas);
      };
    });
  });
}

const generateQrCodes = async (canvasesArray, text, charactersPerCode) => {
  const howManyCodes = Math.ceil(text.length / charactersPerCode);

  let biggest = 0;

  const canvasesGenerating = canvasesArray.map(async (canvas, index) => {
    const startIndex = index * charactersPerCode;
    const endIndex = index === howManyCodes - 1 ? text.length : startIndex + charactersPerCode;
    const chunkToQr = text.substring(startIndex, endIndex);
    // Add order prefix for multi-part codes: SP:1/3|chunkdata
    const chunkWithPrefix = howManyCodes > 1 
      ? `SP:${index + 1}/${howManyCodes}|${chunkToQr}`
      : chunkToQr;
    const newCanvas = await generateQrCode(canvas, chunkWithPrefix);
    if (biggest < newCanvas.height) {
      biggest = newCanvas.height;
    }
    return newCanvas;
  });

  const generatedCanvases = await Promise.all(canvasesGenerating);

  return generatedCanvases;
};

// New function to generate multiple SecurePaper codes with text
const generateQrCodesWithText = async (canvasesArray, text, charactersPerCode, bottomText = 'https://securepaper.app') => {
  const howManyCodes = Math.ceil(text.length / charactersPerCode);

  let biggest = 0;

  const canvasesGenerating = canvasesArray.map(async (canvas, index) => {
    const startIndex = index * charactersPerCode;
    const endIndex = index === howManyCodes - 1 ? text.length : startIndex + charactersPerCode;
    const chunkToQr = text.substring(startIndex, endIndex);
    // Add order prefix for multi-part codes: SP:1/3|chunkdata
    const chunkWithPrefix = howManyCodes > 1 
      ? `SP:${index + 1}/${howManyCodes}|${chunkToQr}`
      : chunkToQr;
    const newCanvas = await generateQrCodeWithText(canvas, chunkWithPrefix, bottomText);
    if (biggest < newCanvas.height) {
      biggest = newCanvas.height;
    }
    return newCanvas;
  });

  const generatedCanvases = await Promise.all(canvasesGenerating);

  return generatedCanvases;
};

// New function to generate multiple SecurePaper codes with logo
const generateQrCodesWithLogo = async (canvasesArray, text, charactersPerCode, logoSrc, logoOptions = {}) => {
  const howManyCodes = Math.ceil(text.length / charactersPerCode);

  let biggest = 0;

  const canvasesGenerating = canvasesArray.map(async (canvas, index) => {
    const startIndex = index * charactersPerCode;
    const endIndex = index === howManyCodes - 1 ? text.length : startIndex + charactersPerCode;
    const chunkToQr = text.substring(startIndex, endIndex);
    // Add order prefix for multi-part codes: SP:1/3|chunkdata
    const chunkWithPrefix = howManyCodes > 1 
      ? `SP:${index + 1}/${howManyCodes}|${chunkToQr}`
      : chunkToQr;
    const newCanvas = await generateQrCodeWithLogo(canvas, chunkWithPrefix, logoSrc, 160, logoOptions);
    if (biggest < newCanvas.height) {
      biggest = newCanvas.height;
    }
    return newCanvas;
  });

  const generatedCanvases = await Promise.all(canvasesGenerating);

  return generatedCanvases;
};

function joinCanvasesToDataUrl(targetCanvas, canvasesArray, columns, rows, width = 160) {
  // Calculate height based on first canvas (in case it has text and is taller)
  const canvasHeight = canvasesArray.length > 0 ? canvasesArray[0].height : width;
  
  targetCanvas.height = rows * canvasHeight;
  targetCanvas.width = columns * width;
  const targetCanvasContext = targetCanvas.getContext('2d');
  targetCanvasContext.fillStyle = '#FFFFFF';
  targetCanvasContext.fillRect(0, 0, targetCanvas.width, targetCanvas.height);

  let lastIndex = 0;
  canvasesArray.forEach((canvas, index) => {
    const inWhichRow = Math.floor(index / columns);
    const inWhichColumn = index % columns;
    const dX = inWhichColumn * width;
    const dY = inWhichRow * canvasHeight;
    targetCanvasContext.drawImage(canvas, dX, dY);
    lastIndex = index;
  });

  for (let c = lastIndex + 1; c < columns * rows; c += 1) {
    const inWhichRow = Math.floor(c / columns);
    const inWhichColumn = c % columns;
    const dX = inWhichColumn * width;
    const dY = inWhichRow * canvasHeight;
    targetCanvasContext.fillRect(dX, dY, width, canvasHeight);
  }

  const dataURL = targetCanvas.toDataURL();

  return { dataURL, width: columns * width, height: rows * canvasHeight };
}


export default {
  generateQrCode,
  generateQrCodes,
  generateQrCodeWithText,
  generateQrCodesWithText,
  generateQrCodeWithLogo,
  generateQrCodesWithLogo,
  joinCanvasesToDataUrl,
};
